import { Alert, Box, Button, Paper, styled, Typography } from '@mui/material'
import React from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom-v5-compat'

import Loading from '@/app/component/guard/loading'
import { useGetOrganization } from '@/app/module/user/hooks/use-get-organization'
import { selectActiveOrg } from '@/app/module/user/store/selectors'
import { hasTotpError } from '@/app/service/util/error'
import { Organization } from '@/app/types'

type TotpGuardProps = {
  children: React.ReactNode
}

const TotpGuard: React.FC<TotpGuardProps> = ({ children }) => {
  const currentOrg = useSelector(selectActiveOrg) as Organization
  const navigate = useNavigate()

  const orgId = currentOrg?.orgId ?? 0
  const { error, isFetching, isLoading, isUninitialized } = useGetOrganization(orgId)

  const navigateTo2FA = () => navigate('/account/2fa')

  if (isUninitialized || isLoading || isFetching) {
    return (
      <Loading isLoading>
        <div />
      </Loading>
    )
  }

  if (!hasTotpError(error)) {
    return children
  }

  return (
    <CenteredContainer>
      <ContentPaper elevation={3}>
        <TitleTypography variant="h5">Two-factor authentication is mandatory</TitleTypography>

        <WarningAlert severity="warning">
          <Typography variant="body1">
            <b>{currentOrg?.name}</b> requires that you set up two-factor authentication (2FA). You can access the
            organization after you set it up.
          </Typography>
        </WarningAlert>

        <SectionHeading variant="h6">Why do I need to do this?</SectionHeading>
        <BodyText variant="body1">
          Currently, all it takes to access your account is your password. That means if a 3rd party were to get access
          to your password or email account, your engageSPARK account would be compromised, too. Fortunately, you can
          easily prevent this.
        </BodyText>

        <BodyTextWithLinks variant="body1">
          Two-factor authentication adds a second layer of security to your user account. When logging in to your
          account, you will now need your password and a "one-time password", generated by your smart phone.{' '}
          <a
            href="https://www.engagespark.com/support/engagespark-two-factor-authentication-2fa/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn more in this support article
          </a>{' '}
          or proceed to the setup here.
        </BodyTextWithLinks>

        <ButtonContainer>
          <Button variant="contained" color="primary" onClick={navigateTo2FA}>
            Set up Two-Factor Authentication
          </Button>
        </ButtonContainer>
      </ContentPaper>
    </CenteredContainer>
  )
}

const CenteredContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  minHeight: '100vh',
  padding: 16,
})

const ContentPaper = styled(Paper)({
  maxWidth: 600,
  width: '100%',
  padding: 24,
})

const TitleTypography = styled(Typography)({
  marginBottom: 16,
  fontWeight: 'bold',
})

const WarningAlert = styled(Alert)({
  marginBottom: 16,
})

const SectionHeading = styled(Typography)({
  marginTop: 24,
  marginBottom: 8,
})

const BodyText = styled(Typography)({
  marginBottom: 16,
})

const BodyTextWithLinks = styled(Typography)({
  marginBottom: 24,
})

const ButtonContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
})

export default TotpGuard
